<template>
  <MenuPopover
    class="LanguageMenu"
    trigger="click"
    placement="bottom"
    align="start"
    :bound-to="{ x: 'container' }"
    :bound-to-selector="within"
    :offset="{ y: 5 }"
    popover-class="popover-default language-menu-popover"
    :label="$t('post_status.language', { language: currentName })"
    :items="items"
    @show="focusSearch"
    @close="query = ''"
  >
    <template #search="{ close }">
      <div class="language-search">
        <input
          ref="search"
          v-model="query"
          type="search"
          class="input"
          :placeholder="$t('post_status.language_search')"
          :aria-label="$t('post_status.language_search')"
          @keydown.enter="onEnter($event, close)"
        >
      </div>
    </template>
    <template #trigger>
      <FAIcon
        icon="language"
        fixed-width
      />
      <span class="label">{{ modelValue ? modelValue.toUpperCase() : $t('post_status.language_auto_short') }}</span>
    </template>
  </MenuPopover>
</template>

<script>
import MenuPopover from 'src/components/menu_popover/menu_popover.vue'

import {
  filterLanguages,
  postLanguages,
} from 'src/services/post_language/post_language.js'

// The language a post is marked with, as a small pill beside the scope.
export default {
  components: { MenuPopover },
  props: {
    modelValue: { type: String, default: null },
    // the menu stays inside this element
    within: { type: String, default: undefined },
  },
  emits: ['update:modelValue'],
  data: () => ({ query: '' }),
  computed: {
    languages() {
      return postLanguages(this.$i18n?.locale ?? 'en', this.modelValue)
    },
    currentName() {
      if (!this.modelValue) return this.$t('post_status.language_auto')
      return (
        this.languages.find((l) => l.code === this.modelValue)?.name ??
        this.modelValue
      )
    },
    matches() {
      return filterLanguages(this.languages, this.query)
    },
    // the search field first; while searching, only what it matches
    items() {
      const search = { key: 'search', slot: 'search' }
      if (this.query.trim()) {
        if (!this.matches.length) {
          return [
            search,
            { key: 'none', heading: this.$t('post_status.language_none') },
          ]
        }
        return [search, ...this.matches.map(this.languageItem)]
      }
      return [
        search,
        { key: 'heading', heading: this.$t('post_status.language_heading') },
        {
          key: 'auto',
          label: this.$t('post_status.language_auto'),
          description: this.$t('post_status.language_auto_description'),
          radio: true,
          checked: !this.modelValue,
          action: () => this.$emit('update:modelValue', null),
        },
        { key: 'd1', divider: true },
        ...this.languages.map(this.languageItem),
      ]
    },
  },
  methods: {
    languageItem({ code, name }) {
      return {
        key: code,
        label: name,
        radio: true,
        checked: code === this.modelValue,
        action: () => this.$emit('update:modelValue', code),
      }
    },
    // Enter: the first match
    // Enter picks the first match; not the Enter that ends a composition
    // (Japanese or Chinese input: it commits the typed word)
    onEnter(event, close) {
      if (event.isComposing || event.keyCode === 229) return
      event.preventDefault()
      this.pickFirst(close)
    },
    pickFirst(close) {
      const first = this.query.trim() && this.matches[0]
      if (!first) return
      this.$emit('update:modelValue', first.code)
      this.query = ''
      close()
    },
    // with a mouse at hand; on a phone the keyboard would cover the list
    focusSearch() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        return
      }
      this.$nextTick(() => this.$refs.search?.focus())
    },
  },
}
</script>

<style lang="scss">
@use "../../mixins";

.LanguageMenu {
  display: inline-flex;
}

/* like the scope pill beside it, but quieter */
.LanguageMenu > .popover-trigger-button {
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  padding: 0.15em 0.55em 0.15em 0.45em;
  border-radius: var(--r-pill);
  box-shadow: inset 0 0 0 1px var(--border);
  color: var(--textFaint);
  font-size: var(--fs-sm);
  line-height: 1.6;
  white-space: nowrap;

  svg {
    color: var(--textFaint);
  }

  .label {
    font-weight: 600;
    letter-spacing: 0.03em;
  }
}

/* a long list: it scrolls instead of running off the screen. The list
   scrolls, not the popover: the popover's frame would scroll with it */
.language-menu-popover > .MenuPopover {
  max-height: min(24em, 60vh);
  overflow: hidden auto;
  border-radius: inherit;

  /* as wide as the full list, so a search does not make it jump */
  min-width: 15em;
}

/* the search stays on top while the list scrolls */
.language-search {
  position: sticky;
  top: -0.25em;
  z-index: 1;
  margin: -0.25em -0.25em 0;
  padding: 0.5em;
  background-color: var(--background);

  .input {
    width: 100%;
    box-sizing: border-box;
  }
}

/* a finger needs a taller pill */
@include mixins.touch {
  .LanguageMenu > .popover-trigger-button {
    min-height: 2.25rem;
  }
}
</style>
